import { useRemoteData } from '@binhatch/hooks';
import { updateApiAccessToken } from '@binhatch/utility';
import React from 'react';
import { createContainer } from 'unstated-next';

import { userApi } from '@/integrations/api';
import { useLocalStorage } from 'react-use';
import { UseAuthResult } from './types';

enum EmbeddedMessageType {
  ReqAccessToken = 'flexinet.req_access_token'
}

interface EmbeddedFlexinetProtocol {
  [EmbeddedMessageType.ReqAccessToken]: [{}, { accessToken: string }];
}

interface EmbeddedMessage {
  id: number;
  type: EmbeddedMessageType;
  payload: any;
}

class EmbeddedFlexinetAuth {
  private id = 0;
  private types = new Set(Object.values(EmbeddedMessageType));
  private map = new Map<number, (data: EmbeddedMessage) => void>();

  constructor() {
    window.addEventListener(
      'message',
      (event: MessageEvent<EmbeddedMessage | undefined>) => {
        if (!event.data) return;

        if (!this.types.has(event.data.type)) return;

        const callback = this.map.get(event.data.id);

        if (!callback) return;

        this.map.delete(event.data.id);

        return callback(event.data);
      },
      false
    );
  }

  private get parent() {
    if (!window.parent) throw new Error('App is not embedded.');

    return window.parent;
  }

  private async send<T extends EmbeddedMessageType>(
    { type, payload }: { type: T; payload: EmbeddedFlexinetProtocol[T][0] },
    timeout: number
  ) {
    const id = ++this.id;

    const message: EmbeddedMessage = { id, type, payload };

    const wait = this.wait<EmbeddedFlexinetProtocol[T][1]>(id, timeout);

    this.parent.postMessage(message, '*');

    return await wait;
  }

  private async wait<T>(id: number, timeout: number): Promise<T> {
    return new Promise((resolve, reject) => {
      const timeoutId = setTimeout(
        () => reject(new Error(`Waiting for response timed out after ${timeout}ms.`)),
        timeout
      );

      this.map.set(id, ({ payload }) => {
        clearTimeout(timeoutId);
        resolve(payload);
      });
    });
  }

  async getAccessToken() {
    const { accessToken } = await this.send({ type: EmbeddedMessageType.ReqAccessToken, payload: {} }, 3000);

    return accessToken;
  }
}

const auth = new EmbeddedFlexinetAuth();

interface Session {
  accessToken: string;
}

const useAuth = (): UseAuthResult => {
  const [session, setAccessToken, clearAccessToken] = useLocalStorage<Session>('flexinet.session');

  const authenticated = !!session?.accessToken;

  const loginData = useRemoteData(
    { key: 'login' },
    async () => {
      const accessToken = await auth.getAccessToken();

      setAccessToken({ accessToken });

      return accessToken;
    },
    { dedupingInterval: 1000, revalidateIfStale: false, shouldRetryOnError: false }
  );

  updateApiAccessToken(session?.accessToken, () => loginData.mutate().then((accessToken) => accessToken!));

  const login = React.useCallback(async () => {
    await loginData.mutate();
  }, [loginData]);

  const logout = React.useCallback(async () => clearAccessToken(), [clearAccessToken]);

  const profile = useRemoteData({ key: 'getProfile', skip: !authenticated, session }, () =>
    userApi.getCurrentUser().then((r) => r.data)
  );

  const loading = loginData.isLoading || profile.isLoading;

  const context = React.useMemo(() => {
    if (!profile.data) return;

    return { user: profile.data.user, client: profile.data.client };
  }, [profile]);

  return React.useMemo(
    () => ({ authenticated, loading, context, login, logout }),
    [loading, authenticated, context, login, logout]
  );
};

export const Auth = createContainer(useAuth);
